import { useContext, useState } from "react";
import { useLocalization } from "@fluent/react";
import { modules, entities } from "byzantine";
import {
  Alert,
  Button,
  Row,
  TextInput,
  Select,
  ResponsiveFlex,
  Tooltip,
} from "@narmi/design_system";
import {
  LimitsForDisplayType,
  MAX_MEMO_LENGTH,
  useMappedEntryDescription,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { MAX_BULK_TITLE_LENGTH } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import Account from "byzantine/src/Account";
import * as formAdapters from "../../../src/adapters";
import ComboAccountSelector from "../../ComboAccountSelector";
import AccountContext from "../../contexts/AccountContext";
import LimitsAndDisclosuresDialog from "../../transfer/ach/LimitsAndDisclosuresDialog";
import SECModal from "../../transfer/ach/SECDialog";

type TemplateFieldsProps = {
  fromAccount: boolean;
  secCode: boolean;
  entryDesc: boolean;
  description: boolean;
};

const PaymentDetailsSection = ({
  limits,
  templateFields,
  isACHCompanyMissing = false,
}: {
  limits: LimitsForDisplayType;
  templateFields?: TemplateFieldsProps;
  isACHCompanyMissing?: boolean;
}) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const [showLimitsDialog, setShowLimitsDialog] = useState(false);
  const [showSECDialog, setShowSECDialog] = useState(false);
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const codes = modules.achPayments.useSECCodes();
  const { mappedOptions } = useMappedEntryDescription();

  const validAccounts = accounts.filter((account: Account) =>
    account.isValidAchTransferSource()
  );

  const toggleLimitsDialog = () => {
    setShowLimitsDialog(!showLimitsDialog);
  };

  const toggleSECDialog = () => {
    setShowSECDialog(!showSECDialog);
  };

  const fromAccountProps = formAdapters.select(form, "fromAccount");
  const achCompanyFieldError = isACHCompanyMissing
    ? l10n.getString("ach-company-missing-field-error")
    : undefined;

  return (
    <>
      <div>
        <h4 className="fontSize--l nds-sans margin--top--xl margin--bottom--xxs">
          {l10n.getString(
            templateFields
              ? "bulk-template-additional-details-title"
              : "ach-payment-payment-details-title"
          )}
        </h4>
        <p className="margin--bottom--l">
          {l10n.getString(
            templateFields
              ? "bulk-template-additional-details-description"
              : "bulk-payment-details-description"
          )}
        </p>
        <ResponsiveFlex gapSize="l">
          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString("bulk-payment-title")}
                maxLength={MAX_BULK_TITLE_LENGTH}
                endContent={
                  <Tooltip
                    text={l10n.getString("bulk-payment-title-tooltip-copy")}
                    maxWidth="235px"
                  >
                    <span className="narmi-icon-info"></span>
                  </Tooltip>
                }
                {...formAdapters.input(form, "title")}
              />
            </Row.Item>
          </Row>

          {/* account selector: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.fromAccount && (
            <div className="margin--bottom--l">
              <Row>
                <Row.Item>
                  {validAccounts.length === 0 ? (
                    <>
                      <div className="margin--bottom--s">
                        <Alert isActive kind="warn" isDismissable={false}>
                          {l10n.getString(
                            "bulk-payment-no-valid-source-account-alert"
                          )}
                        </Alert>
                      </div>
                      <TextInput
                        label={l10n.getString("label-account")}
                        disabled
                      />
                    </>
                  ) : (
                    <ComboAccountSelector
                      label={l10n.getString("label-account")}
                      accounts={validAccounts}
                      isDestination={true}
                      showAddExternalAccountLink={false}
                      {...fromAccountProps}
                      error={achCompanyFieldError || fromAccountProps.error}
                      errorText={
                        achCompanyFieldError || fromAccountProps.errorText
                      }
                    />
                  )}
                </Row.Item>
              </Row>
            </div>
          )}

          {/* SEC Code: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.secCode && (
            <Row>
              <Row.Item>
                <Select
                  label={l10n.getString("label-transaction-type")}
                  {...formAdapters.select(form, "secCode")}
                >
                  {codes.map((code, index) => (
                    <Select.Item key={`account_type_${index}`} value={code}>
                      {code}
                    </Select.Item>
                  ))}
                </Select>
                <div>
                  <Button
                    label={l10n.getString(
                      "ach-payment-learn-more-transaction-types-cta"
                    )}
                    kind="plain"
                    size="xs"
                    testId="bulk-view-transaction-types"
                    onClick={toggleSECDialog}
                  />
                </div>
              </Row.Item>
            </Row>
          )}

          {/* entry description: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.entryDesc && (
            <div className="margin--bottom--l">
              <Row>
                <Row.Item>
                  <Select
                    label={l10n.getString("label-entry-description")}
                    {...formAdapters.select(form, "entryDesc")}
                  >
                    {mappedOptions.map(({ display, value }) => (
                      <Select.Item key={value} value={value}>
                        {display}
                      </Select.Item>
                    ))}
                  </Select>
                </Row.Item>
              </Row>
            </div>
          )}

          {/* internal Note: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.description && (
            <Row>
              <Row.Item>
                <TextInput
                  label={l10n.getString("label-internal-note-optional")}
                  maxLength={MAX_MEMO_LENGTH}
                  {...formAdapters.input(form, "description")}
                />
              </Row.Item>
            </Row>
          )}
        </ResponsiveFlex>
        <LimitsAndDisclosuresDialog
          isOpen={showLimitsDialog}
          handleClose={toggleLimitsDialog}
          limits={limits}
          fromFlow="bulkPayment"
        />
        <SECModal
          isOpen={showSECDialog}
          handleClose={toggleSECDialog}
          codes={codes}
        />
      </div>
    </>
  );
};

export default PaymentDetailsSection;
